import { useMemo } from 'react'
import { GetServerSideProps } from 'next'
import { Card, Skeleton, Empty } from 'antd'
import SimpleBackground from '@/components/SimpleBackground'
import { useMatch } from '@/hooks/swr/useMatch'
import useRooms from '@/hooks/swr/useRoom'
import { useAllRounds } from '@/hooks/swr/useRound'
import { useTeams } from '@/hooks/swr/useTeam'

const groupLabel = (i: number) => String.fromCharCode(65 + i) // 0 -> A

// 签表 / 循环赛矩阵(对标乐动力):每组 队×队 胜负网格 + 胜场排名。
// 数据源:匹克球小组循环赛用 useRooms(每场一房);团队分组循环赛房间跨轮次复用,
// 必须从 all_rounds 取全部对局(否则每组只显示当前轮的两队)。
const GroupTable = ({ match_id }) => {
  const { data: match } = useMatch(match_id)
  const isGroupRoundRobin = match?.match_type === 'group_round_robin'
  const { data: rooms, isValidating } = useRooms(match_id, 'sum', match?.is_encrypted)
  const { data: allRounds } = useAllRounds(isGroupRoundRobin ? match_id : (undefined as any))
  const { data: teams } = useTeams(isGroupRoundRobin ? match_id : (undefined as any), 'sum', match?.is_encrypted)

  const groups = useMemo(() => {
    const teamNameById: Record<string, string> = {}
    ;(teams || []).forEach((t: any) => {
      teamNameById[t.id] = t.name
    })
    const roundRows = (allRounds || [])
      .filter((r: any) => r?.data?.pickleball_phase === 'group' && r.team1_id && r.team2_id)
      .map((r: any) => ({
        pickleball_phase: 'group',
        pickleball_group_index: r.data?.pickleball_group_index ?? 0,
        team1_id: r.team1_id,
        team2_id: r.team2_id,
        team1_name: teamNameById[r.team1_id] ?? r.team1_name,
        team2_name: teamNameById[r.team2_id] ?? r.team2_name,
        winner: r.winner,
      }))
    const rows = isGroupRoundRobin ? roundRows : (rooms || []).filter((r) => r.pickleball_phase === 'group' && r.team1_id && r.team2_id)
    const byGroup: Record<string, any[]> = {}
    rows.forEach((r) => {
      const g = r.pickleball_group_index ?? 0
      if (!byGroup[g]) byGroup[g] = []
      byGroup[g].push(r)
    })
    return Object.keys(byGroup)
      .map((g) => {
        const grows = byGroup[g]
        const teamMap: Record<string, { id: string; name: string }> = {}
        grows.forEach((r) => {
          if (!teamMap[r.team1_id]) teamMap[r.team1_id] = { id: r.team1_id, name: r.team1_name }
          if (!teamMap[r.team2_id]) teamMap[r.team2_id] = { id: r.team2_id, name: r.team2_name }
        })
        const result: Record<string, string | null> = {}
        const wins: Record<string, number> = {}
        Object.keys(teamMap).forEach((id) => {
          wins[id] = 0
        })
        grows.forEach((r) => {
          // winner 是 rounds 的权威列:1=team1 胜,2=team2 胜
          let winner: string | null = null
          if (Number(r.winner) === 1) winner = r.team1_id
          else if (Number(r.winner) === 2) winner = r.team2_id
          if (winner) wins[winner] = (wins[winner] || 0) + 1
          result[`${r.team1_id}|${r.team2_id}`] = winner
          result[`${r.team2_id}|${r.team1_id}`] = winner
        })
        const teams = Object.values(teamMap).sort((a, b) => (wins[b.id] || 0) - (wins[a.id] || 0))
        return { g: Number(g), teams, result, wins }
      })
      .sort((a, b) => a.g - b.g)
  }, [rooms, allRounds, teams, isGroupRoundRobin])

  const cell = (group, rowT, colT) => {
    if (rowT.id === colT.id) return <span className="text-gray-300">—</span>
    const key = `${rowT.id}|${colT.id}`
    if (!(key in group.result)) return <span className="text-gray-300">·</span>
    const winner = group.result[key]
    if (winner === rowT.id) return <span className="font-bold text-green-600">胜</span>
    if (winner === colT.id) return <span className="text-gray-400">负</span>
    return <span className="text-gray-300">·</span>
  }

  return (
    <div className="m-auto h-full w-full">
      <SimpleBackground />
      <div className="m-auto w-[860px] max-w-full overflow-auto px-2 py-12 lg:px-6">
        <div className="mb-6 text-center text-3xl font-bold text-white">签表 · 循环赛</div>
        <Skeleton loading={isValidating}>
          {groups.length === 0 ? (
            <Card>
              <Empty description="暂无循环赛签表数据(仅匹克球小组循环赛支持)" />
            </Card>
          ) : (
            groups.map((group) => (
              <Card key={group.g} className="mb-4" title={`${groupLabel(group.g)} 组`}>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-center text-sm">
                    <thead>
                      <tr className="text-xs text-gray-500">
                        <th className="p-2 text-left">队伍</th>
                        {group.teams.map((t) => (
                          <th key={t.id} className="min-w-[56px] max-w-[80px] truncate p-2">
                            {t.name}
                          </th>
                        ))}
                        <th className="p-2">胜场</th>
                        <th className="p-2">排名</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.teams.map((rowT, ri) => (
                        <tr key={rowT.id} className={ri % 2 ? '' : 'bg-gray-50'}>
                          <td className="max-w-[120px] truncate p-2 text-left font-medium">{rowT.name}</td>
                          {group.teams.map((colT) => (
                            <td key={colT.id} className="p-2">
                              {cell(group, rowT, colT)}
                            </td>
                          ))}
                          <td className="p-2 font-bold">{group.wins[rowT.id] ?? 0}</td>
                          <td className="p-2">{ri + 1}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            ))
          )}
        </Skeleton>
      </div>
    </div>
  )
}

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  return { props: { hide_menu: true, match_id: ctx.query.match_id } }
}

export default GroupTable
